/**
 * Phase-aware cycle insights panel (T19.5 / ADR-0030 §3).
 *
 * Premium + cycle-consent gated. Shows:
 *  - Water-retention pre-emption alert (late-luteal window)
 *  - Phase-tagged macro patterns from the user's own logs
 *  - Additive nudges for the current phase
 *
 * Free users see a teaser + PremiumGate. ED-safe: additive-only, no restriction copy.
 */
import { useTranslation } from 'react-i18next';
import { useCycleConsent, useCycleInsights } from '../hooks/useCycle.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import { PremiumGate } from './PremiumGate.js';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function CycleInsightsPanel() {
  const { t } = useTranslation('cycle');
  const { data: entitlement } = useEntitlement();
  const { data: consents } = useCycleConsent();
  const isPremium = entitlement?.isPremium ?? false;
  const hasCycleConsent =
    consents?.consents?.some((c) => c.purpose === 'cycle' && c.granted) ?? false;

  const { data: insights, isLoading } = useCycleInsights(hasCycleConsent && isPremium);

  // Only render when cycle is opted in
  if (!hasCycleConsent) return null;

  // Free users: teaser
  if (!isPremium) {
    return (
      <div className="mt-6 flex flex-col gap-3">
        <h3 className="text-base font-medium text-ink">{t('insights.title')}</h3>
        <div className="rounded-2xl border border-husk bg-paper px-5 py-4">
          <p className="text-sm font-medium text-ink">{t('insights.teaser.title')}</p>
          <p className="mt-1 text-sm leading-5 text-ink-soft">{t('insights.teaser.body')}</p>
        </div>
        <PremiumGate feature="adaptive_coaching" heading={t('insights.teaser.cta')} />
      </div>
    );
  }

  if (isLoading || !insights) return null;

  const { waterRetentionAlert, phasePatterns, nudges } = insights;
  const hasContent = waterRetentionAlert.active || phasePatterns.length > 0 || nudges.length > 0;

  return (
    <div className="mt-6 flex flex-col gap-4">
      <h3 className="text-base font-medium text-ink">{t('insights.title')}</h3>

      {/* Water-retention pre-emption alert */}
      {waterRetentionAlert.active && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          <p className="text-sm font-medium text-amber-900">{t('insights.waterAlert.title')}</p>
          <p className="mt-1 text-sm leading-5 text-amber-800">{t('insights.waterAlert.body')}</p>
          {waterRetentionAlert.nextPeriodStart && (
            <p className="mt-2 text-xs text-amber-700">
              {t('insights.waterAlert.nextPeriod', {
                date: formatDate(waterRetentionAlert.nextPeriodStart),
              })}
            </p>
          )}
        </div>
      )}

      {/* Additive nudges */}
      {nudges.length > 0 && (
        <div className="flex flex-col gap-2">
          {nudges.map((nudge) => (
            <div
              key={nudge.key}
              className="rounded-xl border border-husk bg-paper px-4 py-3 text-sm text-ink"
            >
              <span className="mr-1.5">{nudge.kind === 'hydration' ? '💧' : '🥗'}</span>
              {t(nudge.key as Parameters<typeof t>[0])}
            </div>
          ))}
        </div>
      )}

      {/* Phase patterns */}
      {phasePatterns.length > 0 && (
        <div className="flex flex-col gap-2">
          {phasePatterns.map((pattern) => (
            <div
              key={pattern.key}
              className="rounded-xl border border-husk bg-paper px-4 py-3 text-sm text-ink"
            >
              📊{' '}
              {t(pattern.key as Parameters<typeof t>[0], {
                ...pattern.params,
              })}
            </div>
          ))}
        </div>
      )}

      {!hasContent && <p className="text-sm text-ink-soft">{t('insights.noPatterns')}</p>}
    </div>
  );
}
